<template>
  <v-container fluid pa-1 class="mt-10">
    <v-form ref="form" v-model="isFormValid" lazy-validation>
      <div class="row d-flex justify-center">
        <div class="col-12 col-md-6">
          <div class="d-flex pt-5 mb-5">
            <h1 class="mx-auto">
              Deal Summary
              <span v-if="isApproved" class="stamp float-end">Approved</span>
              <span v-if="isRejectedDeal" class="stamp float-end rejected">Rejected</span>
            </h1>
          </div>
          <v-divider class="mt-5"></v-divider>
          <SummaryTitle title="Client"></SummaryTitle>
          <SummaryItem itemTitle="Client Name" v-if="!!name">
            <template v-slot:content>
              {{ name }}
            </template>
          </SummaryItem>
          <SummaryItem itemTitle="Invoice Frequency" v-if="!!invoiceFrequencyId">
            <template v-slot:content>
              {{ invoiceFrequencyId | getInvoicingFrequencyLabel }}
            </template>
          </SummaryItem>
          <SummaryTitle title="Candidate"></SummaryTitle>
          <SummaryItem itemTitle="Candidate Name" v-if="!!firstName && !!lastName">
            <template v-slot:content>
              {{ candidateName }}
            </template>
          </SummaryItem>
          <SummaryItem itemTitle="Email" v-if="!!emailAddress">
            <template v-slot:content>
              {{ emailAddress }}
            </template>
          </SummaryItem>
          <ExpensesSummary
            v-if="showExpensesSection"
            :expenses="expenses"
          ></ExpensesSummary>
          <SummaryTitle title="Contractor" v-if="isContractor"></SummaryTitle>
          <SummaryTitle title="Permanent Placement" v-if="isPermPlacement"></SummaryTitle>
          <div v-if="isContractor">
            <SummaryItem itemTitle="Job Title" v-if="!!jobTitle">
              <template v-slot:content>
                {{ jobTitle }}
              </template>
            </SummaryItem>
            <SummaryItem itemTitle="Consultant Type" v-if="!!employeeTypeId">
              <template v-slot:content>
                {{ employeeTypeId | getEmployeeTypeLabel }}
              </template>
            </SummaryItem>
            <SummaryItem itemTitle="Contract Type" v-if="!!contractTypeId">
              <template v-slot:content>
                {{ contractTypeId | getContractTypeLabel }}
              </template>
            </SummaryItem>
            <SummaryItem itemTitle="Contract Length" v-if="isContractToHire && !!lengthOfContract">
              <template v-slot:content>
                {{ lengthOfContract }} months
              </template>
            </SummaryItem>
            <SummaryItem itemTitle="Conversion Fee" v-if="isContractToHire && !!conversionFee">
              <template v-slot:content>
                {{ conversionFee | currency }}
              </template>
            </SummaryItem>
            <SummaryItem itemTitle="Start Date" v-if="!!startAt">
              <template v-slot:content>
                {{ startAt | formatDate }}
              </template>
            </SummaryItem>
            <SummaryItem item-title="End Date" v-if="endAt">
              <template v-slot:content>
                {{ endAt | formatDate }}
              </template>
            </SummaryItem>
            <SummaryItem item-title="Pay Frequency" v-if="!!payFrequencyId">
              <template v-slot:content>
                {{ payFrequencyId | getPayFrequencyLabel }}
              </template>
            </SummaryItem>
            <SummaryItem item-title="Pay Rate" v-if="!!payRate && !!payRateTypeId">
              <template v-slot:content>
                {{ payRate | currency }} {{ payRateTypeId | getPayRateTypeLabel }}
              </template>
            </SummaryItem>
            <SummaryItem item-title="Bill Rate" v-if="!!billRate && !!billRateTypeId">
              <template v-slot:content>
                {{ billRate | currency }} {{ billRateTypeId | getBillRateTypeLabel }}
              </template>
            </SummaryItem>
          </div>
          <div v-if="isPermPlacement">
            <SummaryItem item-title="Job Title" v-if="!!jobTitle">
              <template v-slot:content>
                {{ jobTitle }}
              </template>
            </SummaryItem>
            <SummaryItem item-title="Placement Date" v-if="!!permPlacementDate">
              <template v-slot:content>
                {{ permPlacementDate | formatDate }}
              </template>
            </SummaryItem>
            <SummaryItem item-title="Invoice Date" v-if="permPlacementInvoiceDate">
              <template v-slot:content>
                {{ permPlacementInvoiceDate | formatDate }}
              </template>
            </SummaryItem>
            <SummaryItem item-title="Invoice Due Date" v-if="permPlacementInvoiceDueDate">
              <template v-slot:content>
                {{ permPlacementInvoiceDueDate | formatDate }}
              </template>
            </SummaryItem>
            <SummaryItem item-title="Salary" v-if="!!permPlacementSalary">
              <template v-slot:content>
                {{ permPlacementSalary | currency }}
              </template>
            </SummaryItem>
            <SummaryItem item-title="Net Fee" v-if="!!permPlacementFee && !!permPlacementFeeTypeId">
              <template v-slot:content>
                {{ netFee | currency }}
              </template>
            </SummaryItem>
          </div>
          <CommissionsSummary
            v-if="showCommissionsSection"
            :commissions="commissions"
          >
          </CommissionsSummary>
          <SummaryTitle v-if="notes" title="Rejection Comments"></SummaryTitle>
          <div class="row" v-if="notes">
            <div class="col-12 justify-start px-10">
              <p style="white-space: pre-line" class="text-justify">{{ notes }}</p>
            </div>
          </div>
          <div class="row">
            <div class="col-12 justify-center">
              <ButtonsStepWizardForm
                :isFormValid="isFormValid"
                :createNewDealButtonLabel="createNewDealButtonLabel"
                :cancelButtonLabel="(!showApproveDealButton && !showRejectDealButton && !showCreateDealButton) ? 'Close': 'Cancel'"
                :showApproveDealButton="showApproveDealButton"
                :showRejectDealButton="showRejectDealButton"
                :showCreateDealButton="showCreateDealButton"
                @approveDeal="approveDealHandler"
                @rejectDeal="showRejectedModal = true"
                @createNewDeal="createNewDealHandler"
                @cancel="cancel"
              >
              </ButtonsStepWizardForm>
              <DealRejectedModal
                :showModal="showRejectedModal"
                @reject="handleRejectDeal"
                @closeDialog="showRejectedModal = false"
              >
              </DealRejectedModal>
            </div>
          </div>
        </div>
      </div>
    </v-form>
    <MissingDataTaskModal
      ref="MissingDataTaskModal"
      :userId="responsibleId"
      :dueDate="dueDate"
      :taskNotes="taskNotes"
      @userIdChange="(newValue) => {this.responsibleId = newValue}"
      @dueDateChange="(newValue) => {this.dueDate = newValue}"
      @taskNotesChange="(newValue) => {this.taskNotes = newValue}"
      @createTask="confirmTaskCreation"
      @doNotCreateTask="cancelTaskCreation"
      @closeModal="missingDataTaskCloseModalHandler"
    ></MissingDataTaskModal>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { dealsRoute } from '@/lib/constants/routes'
import { CORP_TO_CORP } from '@/lib/constants/employeeTypes'
import { CONTRACT_TO_HIRE } from '@/lib/constants/contractTypes'
import { SimplePageHeader } from '@/components/pageComponents'
import { required } from '@/lib/formValidations'
import { ButtonsStepWizardForm } from '@/components/buttons'
import { dealStatuses } from '@/lib/enum/dealStatuses'
import { userIsAdmin, getCurrentUser, userHasRoles } from '@/lib/authentication'
import DealRejectedModal from './DealRejectedModal'
import { CONTRACTOR, PERMANENT_PLACEMENT } from '@/lib/constants/dealAssignmentTypes'
import { ConfirmationModal } from '@/components/dialogs'
import { PERCENTAGE } from '@/lib/constants/permPlacementFeeTypes'
import { CREATE_FROM_ZOHO } from '@/lib/constants/dealCreationTypes'
import MissingDataTaskModal from '@/components/deals/dealComponents/MissingDataTaskModal'
import { getBillRateTypeLabel } from '@/lib/display/index'
import { SummaryTitle, SummaryItem, ExpensesSummary, CommissionsSummary } from '@/components/shared/display'
import { ADMIN, MANAGER, SALES_PERSON } from '@/lib/constants/securityRoles'

export default {
  components: {
    ButtonsStepWizardForm,
    SimplePageHeader,
    DealRejectedModal,
    ConfirmationModal,
    MissingDataTaskModal,
    SummaryTitle,
    SummaryItem,
    ExpensesSummary,
    CommissionsSummary
  },
  data: () => ({
    showRejectedModal: false,
    isFormValid: false,
    dealStatuses,
    responsibleId: null,
    dueDate: null,
    taskNotes: null,
    previousStatusId: null
  }),
  computed: {
    ...mapFields('settings', ['payRateSettings']),
    ...mapFields('deal', [
      'id',
      'dealCreationTypeId',
      'dealAssignmentType',
      'statusId',
      'dealClientId',
      'dealCandidateId',
      'dealAssignmentId',
      'createdBy',
      'dataMissingTasks',
      'dealSummary',
      'notes',
      'zRJobOpeningId'
    ]),
    ...mapFields('dealClient',
      [
        'existingClientId',
        'name',
        'invoiceFrequencyId',
        'thirdPartyPortalId',
        'dealClientStakeholderRecipients',
        'vmsFee'
      ]),
    ...mapFields('dealCandidate',
      [
        'existingUserId',
        'firstName',
        'lastName',
        'emailAddress'
      ]),
    ...mapFields('dealAssignment',
      [
        'employeeTypeId',
        'corporateName',
        'ein',
        'contractTypeId',
        'lengthOfContract',
        'conversionFee',
        'payFrequencyId',
        'payRateTypeId',
        'payRate',
        'billRateTypeId',
        'billRate',
        'overtimeEligible',
        'startAt',
        'endAt',
        'jobTitle',
        'permPlacementDate',
        'permPlacementSalary',
        'permPlacementFee',
        'permPlacementFeeTypeId',
        'permPlacementInvoiceDate',
        'permPlacementInvoiceDueDate',
        'commissions',
        'expenses',
        'expensesDeduction',
        'workedHoursAverage',
        'overtimeThreshold',
        'overtimeRate',
        'timeSheetType',
        'clockIPAddress'
      ]),
    isReadOnlyAccess () {
      return !this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER, SALES_PERSON] })
    },
    isReadOnlyDeal () {
      return this.isApproved || this.isReadOnlyAccess
    },
    showCreateDealButton () {
      return (this.dealCreationTypeId === CREATE_FROM_ZOHO && this.dealMissingData.length > 0) ||
        (!userIsAdmin() && (!this.statusId || this.statusId === dealStatuses.Rejected || this.statusId === dealStatuses.InProcess) && !this.isReadOnlyDeal)
    },
    showApproveDealButton () {
      return userIsAdmin() && this.dealMissingData.length === 0 && (this.statusId !== dealStatuses.Approved && this.statusId !== dealStatuses.Rejected) && !this.isReadOnlyDeal
    },
    showRejectDealButton () {
      return userIsAdmin() && this.statusId === dealStatuses.New && !this.isReadOnlyDeal
    },
    candidateName () {
      return `${this.firstName} ${this.lastName}`
    },
    isContractor () {
      return this.dealAssignmentType === CONTRACTOR
    },
    isPermPlacement () {
      return this.dealAssignmentType === PERMANENT_PLACEMENT
    },
    isRejectedDeal () {
      return this.statusId === dealStatuses.Rejected
    },
    isApproved () {
      return this.statusId === dealStatuses.Approved
    },
    isC2C () {
      return this.employeeTypeId === CORP_TO_CORP
    },
    isContractToHire () {
      return this.contractTypeId === CONTRACT_TO_HIRE
    },
    showCommissionsSection () {
      return this.commissions && this.commissions.length > 0
    },
    showExpensesSection () {
      return this.expenses && this.expenses.length > 0
    },
    netFee () {
      return this.permPlacementSalary && this.permPlacementFee && this.permPlacementFeeTypeId
        ? (this.permPlacementFeeTypeId === PERCENTAGE
          ? (this.permPlacementSalary * this.permPlacementFee) / 100 : this.permPlacementFee)
        : null
    },
    clientDataMissing () {
      let clientMissingDataString = []
      if (this.existingClientId === null || this.existingClientId === undefined) {
        clientMissingDataString.push(!this.name ? 'Client Name' : '')
        clientMissingDataString.push(!this.invoiceFrequencyId ? 'Invoice Frequency' : '')
      }
      return clientMissingDataString.filter(i => i !== '')
    },
    candidateDataMissing () {
      let candidateMissingDataString = []
      if (this.existingUserId === null || this.existingUserId === undefined) {
        candidateMissingDataString.push(!this.firstName ? 'Candidate First Name' : '')
        candidateMissingDataString.push(!this.lastName ? 'Candidate Last Name' : '')
        candidateMissingDataString.push(!this.emailAddress ? 'Candidate Email Address' : '')
      }
      return candidateMissingDataString.filter(i => i !== '')
    },
    contractDetailsDataMissing () {
      let contractDetailsDataMissing = []
      if (this.dealAssignmentType === PERMANENT_PLACEMENT) {
        contractDetailsDataMissing.push(!this.jobTitle ? 'Job Title' : '')
        contractDetailsDataMissing.push(!this.permPlacementDate ? 'Placement Date' : '')
        contractDetailsDataMissing.push(!this.permPlacementSalary ? 'Salary' : '')
        contractDetailsDataMissing.push(!this.permPlacementFee ? 'Placement Fee' : '')
        contractDetailsDataMissing.push(!this.permPlacementFeeTypeId ? 'Placement Fee Type' : '')
      } else {
        contractDetailsDataMissing.push(!this.workedHoursAverage ? 'Worked Hours Average' : '')
        contractDetailsDataMissing.push(!this.contractTypeId ? 'Contract Type' : '')
        contractDetailsDataMissing.push(this.contractTypeId === CONTRACT_TO_HIRE && !this.lengthOfContract ? 'Length of Contract' : '')
        contractDetailsDataMissing.push(this.contractTypeId === CONTRACT_TO_HIRE && this.conversionFee == null ? 'Conversion Fee' : '')
        contractDetailsDataMissing.push(!this.employeeTypeId ? 'Consultant Type' : '')
        contractDetailsDataMissing.push(this.employeeTypeId === CORP_TO_CORP && !this.corporateName ? 'Corporate Name' : '')
        contractDetailsDataMissing.push(this.employeeTypeId === CORP_TO_CORP && !this.ein ? 'EIN' : '')
        contractDetailsDataMissing.push(!this.startAt ? 'Start Date' : '')
        contractDetailsDataMissing.push(this.contractTypeId === CONTRACT_TO_HIRE && !this.endAt ? 'End Date' : '')
        contractDetailsDataMissing.push(!this.payFrequencyId ? 'Pay Frequency' : '')
        contractDetailsDataMissing.push(!this.payRateTypeId ? 'Pay Rate Type' : '')
        contractDetailsDataMissing.push(!this.billRateTypeId ? 'Bill Rate Type' : '')
        contractDetailsDataMissing.push(!this.billRate ? 'Bill Rate' : '')
      }
      return contractDetailsDataMissing.filter(i => i !== '')
    },
    dealMissingData () {
      return [...this.clientDataMissing, ...this.candidateDataMissing, ...this.contractDetailsDataMissing]
    },
    createNewDealButtonLabel () {
      return this.dealCreationTypeId === CREATE_FROM_ZOHO && this.dealMissingData.length > 0 ? 'Save and Finish Later' : 'Submit New Deal'
    }
  },
  methods: {
    getCurrentUser,
    userHasRoles,
    ...mapActions('deal', [
      'saveDeal',
      'clearDeal'
    ]),
    ...mapActions('user', ['getUser']),
    createNewDealHandler () {
      if (!this.statusId && this.dealCreationTypeId === CREATE_FROM_ZOHO) {
        this.setDealSummary()
      }
      this.previousStatusId = this.statusId
      this.statusId = this.getNewOrInProcessStatusId()
      if (this.dealMissingData.length > 0) {
        this.$refs.MissingDataTaskModal.showModal(this.dealMissingData)
      } else {
        this.saveDeal().then(() => {
          this.cancel()
        })
      }
    },
    approveDealHandler () {
      this.previousStatusId = this.statusId
      this.statusId = dealStatuses.Approved
      if (this.dealMissingData.length > 0) {
        this.$refs.MissingDataTaskModal.showModal(this.dealMissingData)
      } else {
        this.saveDeal().then(() => {
          this.cancel()
        })
      }
    },
    handleRejectDeal (rejectedNotes) {
      this.previousStatusId = this.statusId
      this.statusId = dealStatuses.Rejected
      let currentUser = getCurrentUser()
      this.showRejectedModal = false
      var today = new Date()
      var currentMonth = (today.getMonth() + 1) < 10 ? '0' + (today.getMonth() + 1).toString() : (today.getMonth() + 1).toString()
      var currentDate = today.getDate() < 10 ? '0' + today.getDate().toString() : today.getDate().toString()
      var currentYear = today.getFullYear().toString()
      rejectedNotes = `${rejectedNotes} (${currentUser.firstName} ${currentUser.lastName} - ${currentMonth}/${currentDate}/${currentYear})`
      if (this.notes) {
        this.notes = `${this.notes} \n${rejectedNotes}`
      } else {
        this.notes = rejectedNotes
      }
      this.saveDeal().then(() => {
        this.cancel()
      })
    },
    missingDataTaskCloseModalHandler () {
      this.statusId = this.previousStatusId
      this.previousStatusId = null
    },
    cancel () {
      this.$router.push({ name: dealsRoute.name })
    },
    confirmTaskCreation () {
      this.dataMissingTasks = {
        createTask: true,
        clientDataMissing: this.clientDataMissing != null && this.clientDataMissing.length > 0,
        candidateDataMissing: this.candidateDataMissing != null && this.candidateDataMissing.length > 0,
        contractDetailsDataMissing: this.contractDetailsDataMissing != null && this.contractDetailsDataMissing.length > 0,
        userId: this.responsibleId,
        dueDate: this.dueDate,
        taskNotes: this.taskNotes
      }
      this.saveDeal().then(() => {
        this.cancel()
      })
    },
    cancelTaskCreation () {
      this.dataMissingTasks = {
        createTask: false,
        clientDataMissing: this.clientDataMissing != null && this.clientDataMissing.length > 0,
        candidateDataMissing: this.candidateDataMissing != null && this.candidateDataMissing.length > 0,
        contractDetailsDataMissing: this.contractDetailsDataMissing != null && this.contractDetailsDataMissing.length > 0,
        userId: this.responsibleId,
        dueDate: this.dueDate,
        taskNotes: this.taskNotes
      }
      this.saveDeal().then(() => {
        this.cancel()
      })
    },
    setDealSummary () {
      // Deal Info
      this.dealSummary.dealCreationTypeId = this.dealCreationTypeId
      this.dealSummary.dealAssignmentType = this.dealAssignmentType
      this.dealSummary.statusId = this.statusId
      this.dealSummary.dealClientId = this.dealClientId
      this.dealSummary.dealCandidateId = this.dealCandidateId
      this.dealSummary.dealAssignmentId = this.dealAssignmentId
      // Client
      this.dealSummary.existingClientId = this.existingClientId
      this.dealSummary.clientName = this.name
      this.dealSummary.invoiceFrequencyId = this.invoiceFrequencyId
      this.dealSummary.thirdPartyPortalId = this.thirdPartyPortalId
      this.dealSummary.vmsFee = this.vmsFee
      // Candidate
      this.dealSummary.existingUserId = this.existingUserId
      this.dealSummary.firstName = this.firstName
      this.dealSummary.lastName = this.lastName
      this.dealSummary.emailAddress = this.emailAddress
      // Perm. Placement
      this.dealSummary.jobTitle = this.jobTitle
      this.dealSummary.permPlacementDate = this.permPlacementDate
      this.dealSummary.permPlacementSalary = this.permPlacementSalary
      this.dealSummary.permPlacementFee = this.permPlacementFee
      this.dealSummary.permPlacementFeeTypeId = this.permPlacementFeeTypeId
      this.dealSummary.permPlacementInvoiceDate = this.permPlacementInvoiceDate
      this.dealSummary.permPlacementInvoiceDueDate = this.permPlacementInvoiceDueDate
      // Contractor
      this.dealSummary.employeeTypeId = this.employeeTypeId
      this.dealSummary.corporateName = this.corporateName
      this.dealSummary.ein = this.ein
      this.dealSummary.contractTypeId = this.contractTypeId
      this.dealSummary.lengthOfContract = this.lengthOfContract
      this.dealSummary.conversionFee = this.conversionFee
      this.dealSummary.payFrequencyId = this.payFrequencyId
      this.dealSummary.payRateTypeId = this.payRateTypeId
      this.dealSummary.payRate = this.payRate
      this.dealSummary.billRateTypeId = this.billRateTypeId
      this.dealSummary.billRate = this.billRate
      this.dealSummary.overtimeEligible = this.overtimeEligible
      this.dealSummary.startAt = this.startAt
      this.dealSummary.endAt = this.endAt
      this.dealSummary.commissions = this.commissions
      this.dealSummary.expenses = this.expenses
      this.dealSummary.workedHoursAverage = this.workedHoursAverage
      this.dealSummary.overtimeThreshold = this.overtimeThreshold
      this.dealSummary.overtimeRate = this.overtimeRate
      this.dealSummary.timeSheetType = this.timeSheetType
      this.dealSummary.clockIPAddress = this.clockIPAddress
      this.dealSummary.dealHasMissingData = this.dealMissingData.length > 0
    },
    getNewOrInProcessStatusId () {
      return this.dealCreationTypeId === CREATE_FROM_ZOHO && this.dealMissingData.length > 0
        ? dealStatuses.InProcess
        : dealStatuses.New
    },
    required,
    getBillRateTypeLabel
  }
}
</script>
<style>
.stamp {
  text-align: center;
  opacity: 0.5;
  transform: rotate(27deg);
  position: absolute;
  display: inline-block;
  color: green;
  padding: 15px;
  width: 15rem;
  background-color: white;
  box-shadow: inset 0px 0px 0px 10px green
}

.rejected {
  color: red !important;
  box-shadow: inset 0px 0px 0px 10px red !important;
}

@media (max-width: 576px) {
  .stamp {
    opacity: 0.3;
    padding: 12px !important;
    top: 5em !important;
    right: 1em;
    transform: rotate(0deg) !important;
  }
}

@media (min-width: 577px) {
  .stamp {
    top: 2em;
    right: 1em;
  }
}

@media (min-width: 768px) {
  .stamp {
    top: 3em;
    right: 1em;
  }
}

@media (min-width: 992px) {
  .stamp {
    top: 3em;
    right: 4em;
  }
}

@media (min-width: 1200px) {
  .stamp {
    top: 3em;
    right: 5em;
  }
}

@media (min-width: 1400px) {
  .stamp {
    top: 3em;
    right: 6em;
  }
}

@media (min-width: 1600px) {
  .stamp {
    top: 3em;
    right: 8em;
  }
}

@media (min-width: 1800px) {
  .stamp {
    top: 3em;
    right: 9em;
  }
}

@media (min-width: 2000px) {
  .stamp {
    top: 3em;
    right: 10em;
  }
}

@media (min-width: 2200px) {
  .stamp {
    top: 3em;
    right: 11em;
  }
}

@media (min-width: 2400px) {
  .stamp {
    top: 3em;
    right: 12em;
  }
}

.stamp:after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-image: url('../../../assets/stampTexture.jpg');
  mix-blend-mode: lighten;
}
</style>
